import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './DiscountCalendarPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const REWARD_CATEGORY_OPTIONS = ['plain', 'poly', 'pure'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const EMPTY_TIER = { buyMeters: 5, freeMeters: 1, rewardCategories: ['plain'], rewardDiscountPercent: 100, segment: 'retail' };
const EMPTY_PRESET = {
  promoMessage: '',
  ladder: { tier2Percent: 4, tier3Percent: 12 },
  dynamicTiers: [{ ...EMPTY_TIER }],
  showTimer: 'default',
  isPeak: false,
};

// A plain checkbox can't express "no opinion, defer to the calendar-wide setting" separately from
// "explicitly off" — hence three states here instead of a boolean. 'default' means "use whatever
// the calendar-wide toggle at the top of this page says"; 'show'/'hide' force it either way for
// just this preset/override, regardless of the calendar-wide setting.
function ShowTimerSelect({ value, onChange }) {
  return (
    <label className="dcal-field">
      Countdown timer
      <select className="dcal-select" value={value ?? 'default'} onChange={(e) => onChange(e.target.value)}>
        <option value="default">Use calendar default</option>
        <option value="show">Always show</option>
        <option value="hide">Always hide</option>
      </select>
    </label>
  );
}

function TierEditor({ tiers, onChange }) {
  const updateTier = (index, field, value) => {
    onChange(tiers.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier)));
  };
  const toggleCategory = (index, category) => {
    const tier = tiers[index];
    const has = tier.rewardCategories.includes(category);
    updateTier(index, 'rewardCategories', has ? tier.rewardCategories.filter((c) => c !== category) : [...tier.rewardCategories, category]);
  };
  const addRow = () => onChange([...tiers, { ...EMPTY_TIER }]);
  const removeRow = (index) => onChange(tiers.filter((_, i) => i !== index));

  return (
    <>
      <table className="dcal-tier-table">
        <thead>
          <tr>
            <th>Buy (m)</th>
            <th>Free (m)</th>
            <th>Reward %</th>
            <th>Categories</th>
            <th>Segment</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((tier, index) => (
            <tr key={index}>
              <td><input type="number" className="dcal-num-input" value={tier.buyMeters} onChange={(e) => updateTier(index, 'buyMeters', Number(e.target.value))} /></td>
              <td><input type="number" className="dcal-num-input" value={tier.freeMeters} onChange={(e) => updateTier(index, 'freeMeters', Number(e.target.value))} /></td>
              <td><input type="number" className="dcal-num-input" min="0" max="100" value={tier.rewardDiscountPercent ?? 100} onChange={(e) => updateTier(index, 'rewardDiscountPercent', Number(e.target.value))} /></td>
              <td>
                <div className="dcal-category-checks">
                  {REWARD_CATEGORY_OPTIONS.map((category) => (
                    <label key={category} className="dcal-category-check">
                      <input type="checkbox" checked={tier.rewardCategories.includes(category)} onChange={() => toggleCategory(index, category)} />
                      {category}
                    </label>
                  ))}
                </div>
              </td>
              <td>
                <select className="dcal-select" value={tier.segment || 'retail'} onChange={(e) => updateTier(index, 'segment', e.target.value)}>
                  <option value="retail">Retail</option>
                  <option value="boutique">Boutique</option>
                  <option value="wholesale">Wholesale</option>
                </select>
              </td>
              <td>
                {tiers.length > 1 && <button type="button" className="dcal-btn dcal-btn-secondary dcal-btn-small" onClick={() => removeRow(index)}>Remove</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="dcal-btn dcal-btn-secondary dcal-btn-small" onClick={addRow}>+ Add tier row</button>
    </>
  );
}

function PresetCard({ name, preset, onRename, onChange, onDelete, canDelete }) {
  return (
    <div className="dcal-rule-card">
      <div className="dcal-rule-card-header">
        <input
          className="dcal-preset-name-input"
          value={name}
          onChange={(e) => onRename(e.target.value)}
        />
        <button type="button" className="dcal-btn dcal-btn-secondary dcal-btn-small" onClick={onDelete} disabled={!canDelete} title={!canDelete ? 'This preset is still referenced by a rotation slot or day assignment.' : ''}>
          Delete
        </button>
      </div>

      <label className="dcal-field">
        Promo message
        <input type="text" value={preset.promoMessage} onChange={(e) => onChange({ ...preset, promoMessage: e.target.value })} />
      </label>

      <div className="dcal-ladder-fields">
        <label className="dcal-field dcal-field-inline">
          2m ladder %
          <input type="number" className="dcal-num-input" value={preset.ladder.tier2Percent} onChange={(e) => onChange({ ...preset, ladder: { ...preset.ladder, tier2Percent: Number(e.target.value) } })} />
        </label>
        <label className="dcal-field dcal-field-inline">
          3m+ ladder %
          <input type="number" className="dcal-num-input" value={preset.ladder.tier3Percent} onChange={(e) => onChange({ ...preset, ladder: { ...preset.ladder, tier3Percent: Number(e.target.value) } })} />
        </label>
      </div>

      <ShowTimerSelect value={preset.showTimer} onChange={(next) => onChange({ ...preset, showTimer: next })} />

      <label className="dcal-checkbox-field">
        <input
          type="checkbox"
          checked={preset.isPeak === true}
          onChange={(e) => onChange({ ...preset, isPeak: e.target.checked })}
        />
        Mark as a "peak" special deal (independent of how it gets assigned to a day)
      </label>

      <TierEditor tiers={preset.dynamicTiers} onChange={(tiers) => onChange({ ...preset, dynamicTiers: tiers })} />
    </div>
  );
}

const EMPTY_OVERRIDE_TIER = { buyMeters: 5, freeMeters: 1, rewardCategories: ['plain'], rewardDiscountPercent: 100, segment: 'retail' };

function AddOverrideForm({ onAdd, saving }) {
  const [date, setDate] = useState('');
  const [promoMessage, setPromoMessage] = useState('');
  const [tier2Percent, setTier2Percent] = useState(4);
  const [tier3Percent, setTier3Percent] = useState(12);
  const [tiers, setTiers] = useState([{ ...EMPTY_OVERRIDE_TIER }]);
  const [showTimer, setShowTimer] = useState('default');
  const [isPeak, setIsPeak] = useState(true);

  const submit = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    onAdd({ date, promoMessage, ladder: { tier2Percent, tier3Percent }, dynamicTiers: tiers, showTimer, isPeak });
  };

  return (
    <div className="dcal-add-override">
      <div className="dcal-override-form-row">
        <label className="dcal-field dcal-field-inline">
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="dcal-field dcal-field-inline dcal-field-wide">
          Promo message
          <input type="text" value={promoMessage} onChange={(e) => setPromoMessage(e.target.value)} placeholder="e.g. Independence Day Special: Best Price + 2m Free" />
        </label>
        <label className="dcal-field dcal-field-inline">
          2m ladder %
          <input type="number" className="dcal-num-input" value={tier2Percent} onChange={(e) => setTier2Percent(Number(e.target.value))} />
        </label>
        <label className="dcal-field dcal-field-inline">
          3m+ ladder %
          <input type="number" className="dcal-num-input" value={tier3Percent} onChange={(e) => setTier3Percent(Number(e.target.value))} />
        </label>
      </div>

      <TierEditor tiers={tiers} onChange={setTiers} />

      <ShowTimerSelect value={showTimer} onChange={setShowTimer} />

      <label className="dcal-checkbox-field">
        <input type="checkbox" checked={isPeak} onChange={(e) => setIsPeak(e.target.checked)} />
        Mark as a "peak" special deal
      </label>

      <button className="dcal-btn dcal-btn-primary" onClick={submit} disabled={saving || !date}>
        {saving ? 'Saving…' : 'Add Override'}
      </button>
    </div>
  );
}

function CalendarGrid({ year, month, days, presetNames, dayOfMonthAssignments, onAssignDay, loading }) {
  const leadingBlanks = useMemo(() => new Date(year, month - 1, 1).getDay(), [year, month]);
  const cells = [...Array(leadingBlanks).fill(null), ...days];

  return (
    <div className="dcal-calendar-grid">
      {WEEKDAYS.map((w) => <div key={w} className="dcal-calendar-weekday">{w.slice(0, 3)}</div>)}
      {cells.map((dayInfo, index) => {
        if (!dayInfo) return <div key={`blank-${index}`} className="dcal-calendar-cell dcal-calendar-cell-blank" />;
        const assigned = dayOfMonthAssignments[String(dayInfo.day)] || '';
        return (
          <div key={dayInfo.date} className={`dcal-calendar-cell ${dayInfo.isPeak ? 'dcal-calendar-cell-peak' : ''}`}>
            <div className="dcal-calendar-day-number">{dayInfo.day}</div>
            <div className="dcal-calendar-source" title={dayInfo.promoMessage}>{dayInfo.source}</div>
            <select
              className="dcal-select dcal-calendar-assign-select"
              value={assigned}
              onChange={(e) => onAssignDay(dayInfo.day, e.target.value)}
            >
              <option value="">— unassigned —</option>
              {presetNames.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
        );
      })}
      {loading && <div className="dcal-calendar-loading-overlay">Loading…</div>}
    </div>
  );
}

export default function DiscountCalendarPage() {
  const { currentUser } = useAuth();

  const [rules, setRules] = useState(null);
  const [isDefault, setIsDefault] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [lastRunSummary, setLastRunSummary] = useState(null);
  const [message, setMessage] = useState(null);
  const [showStaticTiers, setShowStaticTiers] = useState(false);

  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1);
  const [calendarDays, setCalendarDays] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const authedFetch = useCallback(async (url, options = {}) => {
    const token = await currentUser.getIdToken();
    return fetch(`${API_BASE}${url}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, overridesRes] = await Promise.all([
        authedFetch('/api/v1/admin/discount-calendar/rules'),
        authedFetch('/api/v1/admin/discount-calendar/overrides'),
      ]);
      const rulesData = await rulesRes.json();
      const overridesData = await overridesRes.json();
      if (!rulesData.success) throw new Error(rulesData.message || 'Failed to load rules.');
      if (!overridesData.success) throw new Error(overridesData.message || 'Failed to load overrides.');
      setRules(rulesData.rules);
      setIsDefault(Boolean(rulesData.isDefault));
      setOverrides(overridesData.overrides || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadCalendar = useCallback(async (year, month) => {
    setCalendarLoading(true);
    try {
      const res = await authedFetch(`/api/v1/admin/discount-calendar/preview?year=${year}&month=${month}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load calendar preview.');
      setCalendarDays(data.days || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setCalendarLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    if (!loading) loadCalendar(viewYear, viewMonth);
  }, [loading, viewYear, viewMonth, loadCalendar]);

  const saveRules = useCallback(async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await authedFetch('/api/v1/admin/discount-calendar/rules', { method: 'PUT', body: JSON.stringify(rules) });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed.');
      setIsDefault(false);
      setMessage({ type: 'success', text: 'Calendar rules saved.' });
      await loadCalendar(viewYear, viewMonth);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  }, [authedFetch, rules, loadCalendar, viewYear, viewMonth]);

  const runNow = useCallback(async () => {
    setRunningNow(true);
    setMessage(null);
    try {
      const res = await authedFetch('/api/v1/admin/discount-calendar/run-now', { method: 'POST' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Run failed.');
      setLastRunSummary(data.summary);
      setMessage({
        type: 'success',
        text: data.summary.skipped
          ? `No Shopify write needed for "${data.summary.source}" — metafield JSON already matched.`
          : `Applied "${data.summary.source}", valid until ${new Date(data.summary.validUntil).toLocaleString()}.`,
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setRunningNow(false);
    }
  }, [authedFetch]);

  const addOverride = useCallback(async (override) => {
    setSavingOverride(true);
    setMessage(null);
    try {
      const res = await authedFetch('/api/v1/admin/discount-calendar/overrides', { method: 'POST', body: JSON.stringify(override) });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to add override.');
      setMessage({ type: 'success', text: `Override added for ${override.date}.` });
      await Promise.all([loadAll(), loadCalendar(viewYear, viewMonth)]);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSavingOverride(false);
    }
  }, [authedFetch, loadAll, loadCalendar, viewYear, viewMonth]);

  const removeOverride = useCallback(async (date) => {
    try {
      const res = await authedFetch(`/api/v1/admin/discount-calendar/overrides/${encodeURIComponent(date)}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to remove override.');
      setOverrides((prev) => prev.filter((o) => o.date !== date));
      await loadCalendar(viewYear, viewMonth);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }, [authedFetch, loadCalendar, viewYear, viewMonth]);

  // --- Preset editing ---
  const presetNames = rules ? Object.keys(rules.presets) : [];

  const isPresetReferenced = (name) => {
    if (rules.rotationSequence.includes(name)) return true;
    if (Object.values(rules.dayOfWeekAssignments).includes(name)) return true;
    if (Object.values(rules.dayOfMonthAssignments).includes(name)) return true;
    return false;
  };

  const updatePreset = (name, nextPreset) => {
    setRules((prev) => ({ ...prev, presets: { ...prev.presets, [name]: nextPreset } }));
  };

  const renamePreset = (oldName, newName) => {
    if (!newName || newName === oldName || rules.presets[newName]) return;
    setRules((prev) => {
      const nextPresets = { ...prev.presets };
      nextPresets[newName] = nextPresets[oldName];
      delete nextPresets[oldName];
      const renameInMap = (map) => Object.fromEntries(Object.entries(map).map(([k, v]) => [k, v === oldName ? newName : v]));
      return {
        ...prev,
        presets: nextPresets,
        rotationSequence: prev.rotationSequence.map((n) => (n === oldName ? newName : n)),
        dayOfWeekAssignments: renameInMap(prev.dayOfWeekAssignments),
        dayOfMonthAssignments: renameInMap(prev.dayOfMonthAssignments),
      };
    });
  };

  const addPreset = () => {
    let n = 1;
    while (rules.presets[`NewPreset${n}`]) n += 1;
    setRules((prev) => ({ ...prev, presets: { ...prev.presets, [`NewPreset${n}`]: { ...EMPTY_PRESET, dynamicTiers: [{ ...EMPTY_TIER }] } } }));
  };

  const deletePreset = (name) => {
    if (isPresetReferenced(name)) return;
    setRules((prev) => {
      const nextPresets = { ...prev.presets };
      delete nextPresets[name];
      return { ...prev, presets: nextPresets };
    });
  };

  // --- Rotation sequence editing ---
  const updateRotationSlot = (index, name) => {
    setRules((prev) => ({ ...prev, rotationSequence: prev.rotationSequence.map((n, i) => (i === index ? name : n)) }));
  };
  const addRotationSlot = () => {
    setRules((prev) => ({ ...prev, rotationSequence: [...prev.rotationSequence, presetNames[0] || ''] }));
  };
  const removeRotationSlot = (index) => {
    setRules((prev) => ({ ...prev, rotationSequence: prev.rotationSequence.filter((_, i) => i !== index) }));
  };

  // --- Day-of-week / day-of-month assignment editing ---
  const updateDayOfWeekAssignment = (weekday, presetName) => {
    setRules((prev) => {
      const next = { ...prev.dayOfWeekAssignments };
      if (presetName) next[weekday] = presetName;
      else delete next[weekday];
      return { ...prev, dayOfWeekAssignments: next };
    });
  };

  const updateDayOfMonthAssignment = (day, presetName) => {
    setRules((prev) => {
      const next = { ...prev.dayOfMonthAssignments };
      if (presetName) next[String(day)] = presetName;
      else delete next[String(day)];
      return { ...prev, dayOfMonthAssignments: next };
    });
  };

  const updateStaticTiers = (nextTiers) => {
    setRules((prev) => ({ ...prev, staticBxgyTiers: nextTiers }));
  };

  const goToPrevMonth = () => {
    if (viewMonth === 1) { setViewYear((y) => y - 1); setViewMonth(12); }
    else setViewMonth((m) => m - 1);
  };
  const goToNextMonth = () => {
    if (viewMonth === 12) { setViewYear((y) => y + 1); setViewMonth(1); }
    else setViewMonth((m) => m + 1);
  };

  if (loading || !rules) {
    return <div className="dcal-page"><div className="dcal-loading">Loading discount calendar...</div></div>;
  }

  return (
    <div className="dcal-page">
      <div className="dcal-header">
        <div>
          <h1>Discount Calendar</h1>
          <p>Presets, day-of-week/day-of-month assignments, rotation fallback, and one-off festival overrides. This page is the only source of truth — no direct Firestore editing needed.</p>
        </div>
        <div className="dcal-header-actions">
          <button className="dcal-btn dcal-btn-secondary" onClick={runNow} disabled={runningNow}>
            {runningNow ? 'Running…' : "Run Now (test today's config)"}
          </button>
          <button className="dcal-btn dcal-btn-primary" onClick={saveRules} disabled={saving}>
            {saving ? 'Saving…' : 'Save Recurring Rules'}
          </button>
        </div>
      </div>

      {lastRunSummary && (
        <div className="dcal-alert dcal-alert-info">
          Last manual run {lastRunSummary.skipped ? 'skipped Shopify write for' : 'applied'} <strong>{lastRunSummary.source}</strong>, valid until {new Date(lastRunSummary.validUntil).toLocaleString()}.
        </div>
      )}
      {isDefault && <div className="dcal-alert dcal-alert-info">No rules saved yet — showing the default schedule. Click "Save Recurring Rules" to persist it.</div>}
      {message ? <div className={`dcal-alert dcal-alert-${message.type}`}>{message.text}</div> : null}

      <label className="dcal-checkbox-field">
        <input
          type="checkbox"
          checked={rules.showTimer !== false}
          onChange={(e) => setRules((prev) => ({ ...prev, showTimer: e.target.checked }))}
        />
        Show countdown timer on storefront by default, for every day of the month
      </label>
      <p className="dcal-hint-text">
        Applies to any day whose preset (or festival override) leaves its own timer setting on "Use calendar default".
        A preset or override set to "Always show"/"Always hide" overrides this for that specific day only.
      </p>

      <h2 className="dcal-section-title">Calendar — {MONTH_NAMES[viewMonth - 1]} {viewYear}</h2>
      <div className="dcal-calendar-nav">
        <button className="dcal-btn dcal-btn-secondary" onClick={goToPrevMonth}>← Previous</button>
        <button className="dcal-btn dcal-btn-secondary" onClick={goToNextMonth}>Next →</button>
        <span className="dcal-calendar-hint">Pick a preset per day below to create a day-of-month assignment (recurring every month). Remember to Save Recurring Rules after.</span>
      </div>
      <CalendarGrid
        year={viewYear}
        month={viewMonth}
        days={calendarDays}
        presetNames={presetNames}
        dayOfMonthAssignments={rules.dayOfMonthAssignments}
        onAssignDay={updateDayOfMonthAssignment}
        loading={calendarLoading}
      />

      <h2 className="dcal-section-title">Presets</h2>
      <div className="dcal-rule-grid">
        {presetNames.map((name) => (
          <PresetCard
            key={name}
            name={name}
            preset={rules.presets[name]}
            onRename={(newName) => renamePreset(name, newName)}
            onChange={(next) => updatePreset(name, next)}
            onDelete={() => deletePreset(name)}
            canDelete={!isPresetReferenced(name)}
          />
        ))}
      </div>
      <button className="dcal-btn dcal-btn-secondary" onClick={addPreset}>+ Add preset</button>

      <h2 className="dcal-section-title">Rotation Fallback Sequence</h2>
      <p className="dcal-hint-text">Cycled by day-of-month for any day with no override, no day-of-week assignment, and no day-of-month assignment.</p>
      <div className="dcal-rotation-list">
        {rules.rotationSequence.map((name, index) => (
          <div key={index} className="dcal-rotation-slot">
            <select className="dcal-select" value={name} onChange={(e) => updateRotationSlot(index, e.target.value)}>
              {presetNames.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {rules.rotationSequence.length > 1 && (
              <button className="dcal-btn dcal-btn-secondary dcal-btn-small" onClick={() => removeRotationSlot(index)}>Remove</button>
            )}
          </div>
        ))}
        <button className="dcal-btn dcal-btn-secondary dcal-btn-small" onClick={addRotationSlot}>+ Add slot</button>
      </div>

      <h2 className="dcal-section-title">Day-of-Week Assignments</h2>
      <p className="dcal-hint-text">Wins over day-of-month assignments if both would apply to the same date.</p>
      <div className="dcal-weekday-grid">
        {WEEKDAYS.map((weekday) => (
          <label key={weekday} className="dcal-field">
            {weekday}
            <select className="dcal-select" value={rules.dayOfWeekAssignments[weekday] || ''} onChange={(e) => updateDayOfWeekAssignment(weekday, e.target.value)}>
              <option value="">— unassigned —</option>
              {presetNames.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
        ))}
      </div>

      <h2 className="dcal-section-title">
        Static Tiers (20m–999m, always-on)
        <button className="dcal-btn dcal-btn-secondary dcal-toggle-btn" onClick={() => setShowStaticTiers((v) => !v)}>{showStaticTiers ? 'Hide' : 'Show'}</button>
      </h2>
      {showStaticTiers && (
        <div className="dcal-rule-card">
          <TierEditor tiers={rules.staticBxgyTiers} onChange={updateStaticTiers} />
        </div>
      )}

      <h2 className="dcal-section-title">Festival Overrides</h2>
      <div className="dcal-overrides-list">
        {overrides.length === 0 && <p className="dcal-empty-text">No one-off overrides scheduled.</p>}
        {overrides.map((override) => (
          <div key={override.date} className="dcal-override-row">
            <div>
              <strong>{override.date}</strong>
              <p>{override.promoMessage}</p>
            </div>
            <button className="dcal-btn dcal-btn-secondary" onClick={() => removeOverride(override.date)}>Remove</button>
          </div>
        ))}
      </div>
      <div className="dcal-rule-card">
        <div className="dcal-rule-card-header"><strong>Add a new override</strong></div>
        <AddOverrideForm onAdd={addOverride} saving={savingOverride} />
      </div>
    </div>
  );
}
